// Feature 09: Distribution Signal Intelligence Service

import { api } from './api';
import { DistributionSignal, TrafficSpike, SpikeDetectionParams, SpikeAnnotation } from '../types/distributionSignal.types';

/**
 * Detect traffic spikes for a page within a date range
 */
export const detectSpikes = async (
  params: SpikeDetectionParams
): Promise<TrafficSpike[]> => {
  const response = await api.get(`/pages/${params.pageId}/spikes/detect`, {
    params: {
      startDate: params.startDate,
      endDate: params.endDate,
      spikeThreshold: params.spikeThreshold || 3.0,
      minDurationDays: params.minDurationDays || 2,
    },
  });
  return response.data;
};

/**
 * Get detailed distribution signal for a spike (includes GA4 breakdown)
 */
export const getDistributionSignal = async (
  spikeId: string
): Promise<DistributionSignal> => {
  const response = await api.get(`/spikes/${spikeId}/distribution-signal`);
  return response.data;
};

/**
 * Get all distribution signals for a page
 */
export const getPageDistributionSignals = async (
  pageId: string,
  startDate?: string,
  endDate?: string
): Promise<DistributionSignal[]> => {
  const response = await api.get(`/pages/${pageId}/distribution-signals`, {
    params: {
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    },
  });
  return response.data;
};

/**
 * Annotate a spike with context (source, explanation)
 */
export const annotateSpikeSource = async (
  spikeId: string,
  annotation: SpikeAnnotation
): Promise<TrafficSpike> => {
  const response = await api.post(`/spikes/${spikeId}/annotate`, annotation);
  return response.data;
};

/**
 * Get spike detection history
 */
export const getSpikeHistory = async (
  pageId: string,
  limit: number = 30
): Promise<TrafficSpike[]> => {
  const response = await api.get(`/pages/${pageId}/spikes/history`, {
    params: { limit },
  });
  return response.data;
};

/**
 * Recompute distribution signal for a spike
 */
export const recomputeDistributionSignal = async (
  spikeId: string
): Promise<DistributionSignal> => {
  const response = await api.post(`/spikes/${spikeId}/recompute`, null);
  return response.data;
};
